import React, { useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { Box, Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import IconSaveAlt from '@material-ui/icons/SaveAlt';
import styles from './styles';
import DropZone, { dropZoneTypes } from 'now-frontend-shared/components/DropZone';
import { createNewCompany, saveFileUpload } from 'store/actions/companyActions';
import {
  getPreSignedUrls,
  removeAWSDataFile,
  setAWSData,
  setUnloadedFilesExist,
} from 'store/actions/businessFilesActions';

import { acceptFileFormats } from 'now-shared/validation/listing-validation';
import { documentTypes } from 'now-shared/helpers/document-types';
import { hasCompanyInfoSaved, hasCompanyInfoToSubmit } from 'layouts/RegistrationRoute';
import { COMPANY_DASHBOARD } from 'constants/registrationFlow';
import Onboarding from 'layouts/Onboarding';
import DocumentViewLayout from 'now-frontend-shared/layouts/DocumentViewLayout';
import { downloadFileAsFilename } from 'now-frontend-shared/utils/download-helpers';
import { getAgreementName } from 'now-shared/helpers/company-helpers';

function UploadDocumentsView({
  classes,
  AWSData,
  getPreSignedUrls,
  preSignedUrls,
  removeAWSDataFile,
  setAWSData,
  setUnloadedFilesExist,
  company,
  authUser,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [submitted, setSubmitted] = React.useState(false);
  const [dropZonesProcessingFiles, setDropZonesProcessingFiles] = React.useState([]);
  const [dropZonesWithFileNotUploaded, setDropZonesWithFileNotUploaded] = React.useState([]);

  const doesExistFileProcessing = !!dropZonesProcessingFiles.length;
  const doesExistFileNotUploaded = !!dropZonesWithFileNotUploaded.length;
  const doesExistPendingUpload = doesExistFileProcessing || doesExistFileNotUploaded;

  useEffect(
    () => {
      dispatch(setUnloadedFilesExist(doesExistPendingUpload));
    },
    [dispatch, setUnloadedFilesExist, doesExistPendingUpload],
  );

  const refs = {
    [documentTypes.CERTIFIED_ARTICLES_OF_INCORPORATION]: React.useRef(),
    [documentTypes.FORM_W9]: React.useRef(),
  };
  const documents = authUser.company?.documents;
  const w9Download = documents?.find?.(doc => doc.type === documentTypes.FORM_W9)?.downloadUrl;
  const articlesDownload = documents?.find?.(doc => doc.type === documentTypes.CERTIFIED_ARTICLES_OF_INCORPORATION)?.downloadUrl;

  const attachmentOptions = [
    {
      label: (
        <>
          <Box component="span" sx={{ fontWeight: '400', fontSize: '24px' }}>
            One of the following:
          </Box>
          <br />
          Certified Articles of Incorporation / Certificate of Formation / Corporate Resolution or Operating Agreement / Articles of Association / Articles of Organization or Memorandum of Association
        </>
      ),
      dropzonePlaceholderPrompt: null,
      dropzonePlaceholder: (
        <>
          Please upload
          {' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            one of
          </Box>
          {' '}
          the documents listed above
        </>
      ),
      documentType: documentTypes.CERTIFIED_ARTICLES_OF_INCORPORATION,
      downloadUrl: articlesDownload,
      agreementName: 'Certified Articles Of Incorporation',
      documentDisplayedUrl: `${process.env.REACT_APP_API_URL}/certified-articles-pdf`,
    },
    {
      label: (
        <Box component="span" sx={{ fontWeight: '400', fontSize: '24px' }}>
          Form W-9
        </Box>),
      dropzonePlaceholderPrompt: 'Please upload',
      dropzonePlaceholder: 'the document listed above',
      documentType: documentTypes.FORM_W9,
      downloadUrl: w9Download,
      agreementName: 'Form W-9',
      documentDisplayedUrl: `${process.env.REACT_APP_API_URL}/form-w2-pdf`,
    },
  ];

  const dropZoneDocTypes = attachmentOptions.map(option => option.documentType);
  const hasAlreadyUploadedDocuments = dropZoneDocTypes.every(docType => !!documents?.find(v => v.type === docType));

  const errorRequiredMessage = 'The file is required. Please complete uploading by clicking the "Upload" button after dropping files.';
  const errorNotUploadedMessage = 'One or more files are pending upload.';

  const errors = {};

  dropZoneDocTypes.forEach(docType => {
    if (!AWSData.some(v => v.type === docType)) {
      errors[docType] = errorRequiredMessage;
    } else if (
      dropZonesProcessingFiles.includes(docType)
      || dropZonesWithFileNotUploaded.includes(docType)
    ) {
      errors[docType] = errorNotUploadedMessage;
    }
  });

  const createAttachments = list => (
    <ol className={classes.itemContainer}>
      {list.map(({
        label,
        dropzonePlaceholderPrompt,
        dropzonePlaceholder,
        documentType,
        downloadUrl,
        agreementName,
        documentDisplayedUrl,
      }) => (
        <div key={documentType} ref={refs[documentType] || null}>
          <li className={classes.itemContainer_li}>
            <Typography
              variant="body1"
              component="div"
              className={classes.liText}
            >
              {label}
            </Typography>
          </li>
          <div className={classes.dropZoneContainer}>
            {hasAlreadyUploadedDocuments
              ? (
                <>
                  <DocumentViewLayout
                    document={downloadUrl}
                    attachments={[]}
                  />
                  <div className={classes.downloadContainer}>
                    <a
                      onClick={e => {
                        e.preventDefault();
                        downloadFileAsFilename({
                          downloadUrl,
                          filename: getAgreementName({
                            baseName: agreementName,
                            isCustom: false,
                            withExtension: true,
                          }),
                        });
                      }}
                      href={documentDisplayedUrl}
                      className={classes.download}
                      download
                    >
                      {agreementName}
                      <IconSaveAlt />
                    </a>
                  </div>
                </>
              )
              : (
                <DropZone
                  AWSData={AWSData}
                  preSignedUrls={preSignedUrls}
                  setAWSData={setAWSData}
                  documentType={documentType}
                  getPreSignedUrls={getPreSignedUrls}
                  removeAWSDataFile={removeAWSDataFile}
                  onSetIsSomeFileNotUploaded={flag => {
                    if (flag) {
                      setDropZonesWithFileNotUploaded(dropZones => [...dropZones, documentType]);
                    } else {
                      setDropZonesWithFileNotUploaded(dropZones => dropZones.filter(dropZone => dropZone !== documentType));
                    }
                  }}
                  accept={acceptFileFormats}
                  onSetIsProcessingFiles={flag => {
                    if (flag) {
                      setDropZonesProcessingFiles(dropZones => [...dropZones, documentType]);
                    } else {
                      setDropZonesProcessingFiles(dropZones => dropZones.filter(dropZone => dropZone !== documentType));
                    }
                  }}
                  placeholderPrompt={dropzonePlaceholderPrompt}
                  placeholder={dropzonePlaceholder}
                  disabled={doesExistFileProcessing}
                />
              )}
          </div>

          {submitted && errors[documentType] && <Typography variant="body2" color="error" style={{ marginTop: 4 }}>{errors[documentType]}</Typography>}
        </div>
      ))}
    </ol>
  );

  const scrollTo = top => window.scrollTo({
    top,
    left: 0,
    behavior: 'smooth',
  });

  const onNext = async () => {
    if (hasAlreadyUploadedDocuments) {
      history.push(COMPANY_DASHBOARD);
      return;
    }

    setSubmitted(true);

    const docTypeWithError = dropZoneDocTypes.find(docType => !!errors[docType]);

    if (docTypeWithError) {
      scrollTo(refs[docTypeWithError].current.offsetTop);
      return;
    }

    await new Promise((resolve, reject) => dispatch(saveFileUpload({
      AWSData,
      resolve,
      reject,
    })));

    setSubmitted(false);

    history.push(COMPANY_DASHBOARD);
  };

  const authUserRef = useRef();
  authUserRef.current = authUser;

  const companyRef = useRef();
  companyRef.current = company;

  React.useEffect(() => {
    if (!hasCompanyInfoSaved(authUserRef.current) && hasCompanyInfoToSubmit(companyRef.current)) {
      dispatch(createNewCompany());
    }
  }, [dispatch]);

  return (
    <Onboarding
      title="Upload Documents"
      subTitle="Please upload the following documents"
      nextDisabled={hasAlreadyUploadedDocuments}
      nextClick={onNext}
      nextText="Submit"
      backButton
    >
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        direction="column"
        style={{ marginRight: 'auto', maxWidth: '840px' }}
      >
        {createAttachments(attachmentOptions)}
      </Grid>
    </Onboarding>
  );
}

UploadDocumentsView.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  AWSData: dropZoneTypes.AWSData.isRequired,
  getPreSignedUrls: dropZoneTypes.getPreSignedUrls.isRequired,
  preSignedUrls: dropZoneTypes.preSignedUrls,
  removeAWSDataFile: dropZoneTypes.removeAWSDataFile.isRequired,
  setAWSData: dropZoneTypes.setAWSData.isRequired,
  setUnloadedFilesExist: PropTypes.func.isRequired,
  authUser: PropTypes.objectOf(PropTypes.any),
};

UploadDocumentsView.defaultProps = {
  authUser: undefined,
  preSignedUrls: undefined,
};

const mapStateToProps = ({ auth, businessFiles, company }) => ({
  AWSData: businessFiles.AWSData,
  AWSDataIsSet: businessFiles.AWSDataIsSet,
  currentListing: businessFiles.currentProperty,
  getPreSignedUrls,
  preSignedUrls: businessFiles.preSignedUrls,
  removeAWSDataFile,
  setAWSData,
  setUnloadedFilesExist,
  company,
  authUser: auth.user,
});

export default compose(
  connect(mapStateToProps),
  withStyles(styles),
)(UploadDocumentsView);
